import { Dropdown, Form } from "react-bootstrap";
import { COLOR } from "../config/constraits";
import { useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSentiments } from "../services/api/sentimentApi";
import { SentimentSelectItem } from "../types/SentimentSelectItem";
import { useTranslation } from "react-i18next";
import "./SentimentIndicatorSelector.css";

interface SentimentIndicatorSingleProps {
    multiSelect?: false;
    value: number | null;
    onChange?: (value: number | null) => void;
}

interface SentimentIndicatorMultiProps {
    multiSelect: true;
    values: number[];
    onChange?: (values: number[]) => void;
}

type SentimentIndicatorProps = (SentimentIndicatorSingleProps | SentimentIndicatorMultiProps) & {
    disabled?: boolean;
    label?: string;
    showSentimentLabel?: boolean;
    showColorDot?: boolean;
    minValue?: number;
    maxValue?: number;
    forceRangeOptions?: boolean;
    sentiments?: SentimentSelectItem[];
};

export default function SentimentIndicator(props: SentimentIndicatorProps) {
    const { t } = useTranslation("claim");
    const {
    disabled = false,
    label,
    showSentimentLabel = true,
    showColorDot = true,
    minValue,
    maxValue,
        forceRangeOptions = false,
        sentiments: propSentiments,
    } = props;
    const multiToggleRef = useRef<HTMLButtonElement | null>(null);
    const singleToggleRef = useRef<HTMLButtonElement | null>(null);

    const { data: fetchedSentiments = [] } = useQuery({
        queryKey: ['sentiments'],
        queryFn: getSentiments,
        enabled: propSentiments === undefined,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
    const dbSentiments = propSentiments ?? fetchedSentiments;

    const effectiveMin = typeof minValue === "number" ? minValue : 1;
    const effectiveMax = typeof maxValue === "number" ? maxValue : 5;
    const normalizedMin = Math.min(effectiveMin, effectiveMax);
    const normalizedMax = Math.max(effectiveMin, effectiveMax);

    const rangeSize = normalizedMax - normalizedMin + 1;
    const firstCut = normalizedMin + Math.floor(rangeSize / 3);
    const secondCut = normalizedMin + Math.floor((2 * rangeSize) / 3);



    const getSentimentColor = (sentimentValue: number) => {
        if (sentimentValue >= secondCut) return COLOR.VERD;
        if (sentimentValue >= firstCut) return COLOR.TARONJA;
        return COLOR.VERMELL;
    };

    const getSentimentLabel = (sentimentValue: number) => {
        if (sentimentValue >= secondCut) return t("newClaimMessageFeelingPositive");
        if (sentimentValue >= firstCut) return t("newClaimMessageFeelingNeutral");
        return t("newClaimMessageFeelingNegative");
    };

    const dbSentimentOptions = useMemo(() => {
        if (!dbSentiments.length) return [];

        const now = Date.now();
        const byValue = new Map<number, { value: number; type: string; startDate: number }>();
        for (const sentiment of dbSentiments) {
            const parsedStart = Date.parse(sentiment.startDate ?? "");
            const startDate = Number.isNaN(parsedStart) ? 0 : parsedStart;
            if (startDate > now) continue;
            if (sentiment.endDate) {
                const endDate = Date.parse(sentiment.endDate);
                if (!Number.isNaN(endDate) && endDate < now) continue;
            }
            const current = byValue.get(sentiment.value);
            if (!current || startDate >= current.startDate) {
                byValue.set(sentiment.value, {
                    value: sentiment.value,
                    type: (sentiment.type ?? "").toLowerCase(),
                    startDate,
                });
            }
        }

        return Array.from(byValue.values())
            .filter((item) => item.value >= normalizedMin && item.value <= normalizedMax)
            .sort((a, b) => a.value - b.value);
    }, [dbSentiments, normalizedMin, normalizedMax]);

    const dbTypeByValue = useMemo(() => {
        const map = new Map<number, string>();
        for (const item of dbSentimentOptions) {
            map.set(item.value, item.type);
        }
        return map;
    }, [dbSentimentOptions]);

    const getTypeColor = (type: string, sentimentValue: number) => {
        if (type === "positive") {
            return COLOR.VERD;
        }
        if (type === "neutral") {
            return COLOR.TARONJA;
        }
        if (type === "negative") {
            return COLOR.VERMELL;
        }
        return getSentimentColor(sentimentValue);
    };

    const getTypeLabel = (type: string, sentimentValue: number) => {
        if (type === "positive") return t("newClaimMessageFeelingPositive");
        if (type === "neutral") return t("newClaimMessageFeelingNeutral");
        if (type === "negative") return t("newClaimMessageFeelingNegative");
        return getSentimentLabel(sentimentValue);
    };

    const sentimentOptions = forceRangeOptions
        ? Array.from({ length: normalizedMax - normalizedMin + 1 }, (_, index) => {
            const optionValue = normalizedMin + index;
            const optionType = (dbTypeByValue.get(optionValue) ?? "").toLowerCase();

            return {
                value: optionValue,
                label: showSentimentLabel ? `${optionValue} - ${getTypeLabel(optionType, optionValue)}` : String(optionValue),
                color: getTypeColor(optionType, optionValue)
            };
        })
        : dbSentimentOptions.map((option) => {
            const optionType = (option.type ?? "").toLowerCase();
            return {
                value: option.value,
                label: showSentimentLabel ? `${option.value} - ${getTypeLabel(optionType, option.value)}` : String(option.value),
                color: getTypeColor(optionType, option.value)
            };
        });

    // Multi-select mode
    if (props.multiSelect) {
        const selectedValues = props.values;
        const toggleValue = (val: number) => {
            const next = selectedValues.includes(val)
                ? selectedValues.filter(v => v !== val)
                : [...selectedValues, val];
            props.onChange?.(next);
        };
        const clearAll = () => props.onChange?.([]);

        const toggleLabel = selectedValues.length === 0
            ? "-"
            : selectedValues.length === 1
                ? sentimentOptions.find(o => o.value === selectedValues[0])?.label ?? String(selectedValues[0])
                : `${selectedValues.length} selected`;

        return (
            <Form.Group>
                {label && <Form.Label>{label}</Form.Label>}
                <Dropdown autoClose="outside" drop="down">
                    <Dropdown.Toggle
                        ref={multiToggleRef}
                        variant="light"
                        className={`form-select dropdown-no-caret dropdown-select d-flex align-items-center justify-content-between text-start sentiment-dropdown-toggle${disabled ? " sentiment-dropdown-toggle-disabled" : ""}`}
                        disabled={disabled}
                    >
                        <span className="sentiment-toggle-content">
                            {showColorDot && (
                                <span
                                    className={`sentiment-dot${selectedValues.length === 1 ? "" : " sentiment-dot-empty"}`}
                                    style={{
                                        backgroundColor: selectedValues.length === 1
                                            ? sentimentOptions.find(o => o.value === selectedValues[0])?.color ?? "#e0e0e0"
                                            : "#e0e0e0",
                                    }}
                                />
                            )}
                            {toggleLabel}
                        </span>
                    </Dropdown.Toggle>

                    <Dropdown.Menu
                        className="dropdown-menu-text-black sentiment-dropdown-menu"
                        align="start"
                        renderOnMount
                        popperConfig={{ strategy: "fixed" }}
                        style={{ width: multiToggleRef.current?.offsetWidth }}
                        onClick={e => e.stopPropagation()}
                    >
                        <Dropdown.Item onClick={clearAll}>-</Dropdown.Item>
                        {sentimentOptions.map((option) => (
                            <Dropdown.Item
                                key={option.value}
                                onClick={() => toggleValue(option.value)}
                                className="d-flex align-items-center gap-2"
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option.value)}
                                    onChange={() => toggleValue(option.value)}
                                    onClick={e => e.stopPropagation()}
                                    className="sentiment-checkbox"
                                />
                                {showColorDot && (
                                    <span
                                        className="sentiment-dot"
                                        style={{ backgroundColor: option.color }}
                                    />
                                )}
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </Form.Group>
        );
    }

    // Single-select mode (existing behaviour)
    const numericValue =
        props.value !== null
            ? Number(props.value)
            : null;

    const selectedOption = sentimentOptions.find(
        opt => opt.value === numericValue
    );

    const selectedLabel =
        props.value === null
            ? "-"
            : selectedOption?.label ?? String(props.value);

    return (
        <Form.Group>
            {label && <Form.Label>{t(label)}</Form.Label>}
            <Dropdown drop="down">
                <Dropdown.Toggle
                    ref={singleToggleRef}
                    variant="light"
                    className={`form-select dropdown-no-caret dropdown-select d-flex align-items-center justify-content-between text-start sentiment-dropdown-toggle${disabled ? " sentiment-dropdown-toggle-disabled" : ""}`}
                    disabled={disabled}
                >
                    <span className="sentiment-toggle-content">
                        {showColorDot && (
                            <span
                                className={`sentiment-dot${selectedOption ? "" : " sentiment-dot-empty"}`}
                                style={{ backgroundColor: selectedOption?.color || "#e0e0e0" }}
                            />
                        )}
                        {t(selectedLabel)}
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                    className="dropdown-menu-text-black sentiment-dropdown-menu"
                    align="start"
                    renderOnMount
                    popperConfig={{ strategy: "fixed" }}
                    style={{ width: singleToggleRef.current?.offsetWidth }}
                >
                    <Dropdown.Item onClick={() => props.onChange?.(null)}>-</Dropdown.Item>
                    {sentimentOptions.map((option) => (
                        <Dropdown.Item
                            key={option.value}
                            onClick={() => props.onChange?.(option.value)}
                            className="d-flex align-items-center gap-2"
                        >
                            {showColorDot && (
                                <span
                                    className="sentiment-dot"
                                    style={{ backgroundColor: option.color }}
                                />
                            )}
                            {option.label}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
        </Form.Group>
    );
}
